import React, { useMemo } from "react"
import { Alert, Button, Card, Empty, Modal, Row, Col, Select, Tag } from "antd"
import { googleMapsServerApiKey } from "./utils"

const { Option } = Select

export interface IPoolSelectionClub {
  clubId: number
  clubName: string
}

export interface IPoolSelectionOption {
  poolId: number
  poolName: string
  address: string | null
  length: number | null
  course: "SCY" | "SCM" | "LCM" | null
  clubId: number | null
  clubName: string | null
  description?: string | null
  lat?: number | null
  lng?: number | null
  unitOfMeasure?: number | null
  numLanes?: number | null
  primaryPool?: boolean
}

interface IPoolSelectionModalProps {
  visible: boolean
  title: string
  clubs: IPoolSelectionClub[]
  pools: IPoolSelectionOption[]
  genericPools?: IPoolSelectionOption[]
  selectedClubId: number | null
  selectedPoolId: number | null
  onChangeClub: (clubId: number) => void
  onSelectPool: (pool: IPoolSelectionOption) => void
  onCancel: () => void
  confirmButtonText?: string
  onConfirmSelection?: () => void
  confirmDisabled?: boolean
  confirmLoading?: boolean
  supplementalContent?: React.ReactNode
  allowedPoolCreateClubId?: number | null
  onCreatePoolForSelectedClub?: () => void
  poolCreateBlockedReason?: string | null
}

const unitLabelMap: Record<number, string> = {
  0: "yd",
  1: "m",
  2: "m",
}

const buildPoolLengthLabel = (pool: IPoolSelectionOption) => {
  if (pool.length === null || pool.length === undefined) {
    return pool.course || "-"
  }

  const explicitUnit =
    typeof pool.unitOfMeasure === "number"
      ? unitLabelMap[pool.unitOfMeasure]
      : null
  const fallbackUnit =
    pool.course === "SCY"
      ? "yd"
      : pool.course === "SCM" || pool.course === "LCM"
        ? "m"
        : null
  const unitLabel = explicitUnit || fallbackUnit

  return `${pool.course || "Custom"} · ${pool.length}${unitLabel ? ` ${unitLabel}` : ""}`
}

export default function PoolSelectionModal({
  visible,
  title,
  clubs,
  pools,
  genericPools = [],
  selectedClubId,
  selectedPoolId,
  onChangeClub,
  onSelectPool,
  onCancel,
  confirmButtonText,
  onConfirmSelection,
  confirmDisabled = false,
  confirmLoading = false,
  supplementalContent,
  allowedPoolCreateClubId = null,
  onCreatePoolForSelectedClub,
  poolCreateBlockedReason = null,
}: IPoolSelectionModalProps): JSX.Element {
  const selectedClub = useMemo(
    () => clubs.find((club) => club.clubId === selectedClubId) || null,
    [clubs, selectedClubId],
  )
  const selectedClubPools = useMemo(
    () =>
      pools.filter(
        (pool) => selectedClubId !== null && pool.clubId === selectedClubId,
      ),
    [pools, selectedClubId],
  )
  const canCreatePoolForSelectedClub =
    selectedClubId !== null &&
    allowedPoolCreateClubId !== null &&
    selectedClubId === allowedPoolCreateClubId &&
    !poolCreateBlockedReason &&
    Boolean(onCreatePoolForSelectedClub)
  const selectedClubGenericPools = useMemo(
    () =>
      genericPools.map((pool) => ({
        ...pool,
        clubId: selectedClubId,
        clubName: selectedClub?.clubName || null,
      })),
    [genericPools, selectedClub?.clubName, selectedClubId],
  )

  const renderPoolCard = (pool: IPoolSelectionOption, isGeneric = false) => {
    const isSelected = selectedPoolId === pool.poolId

    return (
      <Col xs={24} md={12} key={`${isGeneric ? "generic" : "pool"}_picker_card_${pool.poolId}_${pool.course || "unknown"}`}>
        <Card
          hoverable
          onClick={() => onSelectPool(pool)}
          style={{
            borderColor: isSelected ? "var(--primaryblue)" : undefined,
            boxShadow: isSelected
              ? "0 0 0 2px rgba(24, 144, 255, 0.14)"
              : undefined,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{pool.poolName}</div>
              <div style={{ color: "rgba(0,0,0,0.65)" }}>{pool.clubName}</div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                alignSelf: "flex-start",
              }}
            >
              {isGeneric ? <Tag>Generic Option</Tag> : null}
              {pool.primaryPool ? <Tag color="blue">Primary Competition Pool</Tag> : null}
              {isSelected ? <Tag color="green">Selected</Tag> : null}
            </div>
          </div>

          {pool.description ? (
            <div style={{ marginBottom: 8, color: "rgba(0,0,0,0.65)" }}>{pool.description}</div>
          ) : null}

          <div style={{ marginBottom: 8 }}>
            <strong>Course:</strong> {buildPoolLengthLabel(pool)}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Number of lanes:</strong> {pool.numLanes ?? "-"}
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Address:</strong> {pool.address || "No address stored"}
          </div>

          {pool.lat != null && pool.lng != null ? (
            <img
              alt={`${pool.poolName} map`}
              style={{ width: "100%", borderRadius: 4, marginBottom: 12 }}
              src={`https://maps.googleapis.com/maps/api/staticmap?center=${pool.lat},${pool.lng}&zoom=13&size=600x300&maptype=roadmap&markers=color:blue%7Clabel:P%7C${pool.lat},${pool.lng}&key=${googleMapsServerApiKey}`}
            />
          ) : null}

          <Button type={isSelected ? "primary" : "default"} block onClick={() => onSelectPool(pool)}>
            {isSelected ? "Selected Pool" : "Choose This Pool"}
          </Button>
        </Card>
      </Col>
    )
  }

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        ...(onConfirmSelection
          ? [
              <Button
                key="confirm"
                type="primary"
                onClick={onConfirmSelection}
                disabled={confirmDisabled}
                loading={confirmLoading}
              >
                {confirmButtonText || "Confirm"}
              </Button>,
            ]
          : []),
      ]}
      width={960}
      destroyOnHidden={true}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Choose a pool stored from the following club."
        description="After you choose a club, select one of that club's stored pools to use for this meet."
      />

      <div style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 8, fontWeight: 600 }}>
          Choose a pool stored from the following club
        </div>
        <Select
          style={{ width: "100%" }}
          value={selectedClubId ?? undefined}
          placeholder="Choose club"
          onChange={onChangeClub}
        >
          {clubs.map((club) => (
            <Option key={`pool_picker_club_${club.clubId}`} value={club.clubId}>
              {club.clubName}
            </Option>
          ))}
        </Select>
      </div>

      {supplementalContent ? (
        <div style={{ marginBottom: 16 }}>{supplementalContent}</div>
      ) : null}

      {selectedClubId === null ? (
        <Empty description="Choose a club to see its stored pools." />
      ) : selectedClubPools.length === 0 ? (
        <div>
          <Empty description="This club does not have any stored pools yet." />
          <Alert
            type="warning"
            showIcon
            title="A pool is required before this meet can be created."
            description={
              canCreatePoolForSelectedClub ? (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    Create a pool for {selectedClub?.clubName || "this club"} now, then select it to finish creating the meet.
                  </div>
                  <Button type="primary" onClick={onCreatePoolForSelectedClub}>
                    Add Pool For {selectedClub?.clubName || "This Club"}
                  </Button>
                </div>
              ) : (
                poolCreateBlockedReason ||
                "Only your acting club can add pools from this meet-creation flow. Select your club to add a pool here, or ask this club's admin to add one."
              )
            }
          />
          {selectedClubGenericPools.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  marginBottom: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#8c8c8c",
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                }}
              >
                Generic Pool Options
              </div>
              <div style={{ marginBottom: 12, color: "rgba(0,0,0,0.65)" }}>
                Use one of these fallback pool records when the facility for this meet is not using Swimmingly.
              </div>
              <Row gutter={[16, 16]}>
                {selectedClubGenericPools.map((pool) => renderPoolCard(pool, true))}
              </Row>
            </div>
          ) : null}
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {selectedClubPools.map((pool) => renderPoolCard(pool))}
        </Row>
      )}
    </Modal>
  )
}